import SoundsSearcher from './SoundsSearcher';
import TagVarietyReducer from './TagVarietyReducer.js';
import {OR} from 'search/constants.js';
import {AND} from 'search/constants.js';
import {TAG} from 'constants/field-types.js';
import {YOUTUBE} from 'constants/field-types.js';
import {STYLE} from 'constants/field-types.js';
import {COUNTRY} from 'constants/field-types.js';
import {YEAR} from 'constants/field-types.js';
import {DISCOGS} from 'constants/field-types.js';
import ethereum from 'decentralized/ethereum.js';

const HUGE_NUMBER = 10000000;

class EthereumFieldSearcher extends SoundsSearcher {

   constructor(cache) {
       super();

       this.cache = cache;
       this.varietyReducer = new TagVarietyReducer();
   }

    freeTextSearch(searchString) {
        return new Promise((resolve, reject) => {
            resolve({
                TAG: normalize(this.cache.freeTextSearch(TAG, searchString)),
                STYLE: normalize(this.cache.freeTextSearch(STYLE, searchString)),
                COUNTRY: normalize(this.cache.freeTextSearch(COUNTRY, searchString)),
                YEAR: normalize(this.cache.freeTextSearch(YEAR, searchString)),
            });
        });
    }

   async getTopVideos(start, size) {
        if (this.empty()) {
            return this.cache.getAllForField(YOUTUBE, 0, HUGE_NUMBER);
        } else {
            let condition = this.selectedOrTags.length > 0 ? OR : AND;
            return this.getTopFields(0, HUGE_NUMBER, YOUTUBE, condition);
        }
    }

   async getTopDiscogs(start, size) {
        if (this.empty()) {
            return this.cache.getAllForField(DISCOGS, 0, HUGE_NUMBER);
        } else {
            let condition = this.selectedOrTags.length > 0 ? OR : AND;
            return this.getTopFields(0, HUGE_NUMBER, DISCOGS, condition);
        }
    }

    // returns a list of objects, each w/ a "value" field 
    // (containing the tag) and a "rank" field containing
    // the ranking
    async getTopTags(start, size, selectedTags) {
        selectedTags = selectedTags === undefined ? this.selectedTags : selectedTags;
        if (this.filterFavorites) {
            // get the sounds
            let array = await ethereum.getFavoritesForUser();
            return this.getTagsForSounds(array);
        }

        console.log('selected tags = ', selectedTags);
        let t = await this.getTopFields(start, size, TAG);
        return this.varietyReducer.reduce(t, selectedTags);
   }

    async getTopStyles() {
        let condition = this.selectedOrTags.length > 0 ? OR : AND;
        return this.getTopFields(undefined, undefined, STYLE, condition);
    }

    async getTopCountries() {
        let condition = this.selectedOrTags.length > 0 ? OR : AND;
        return this.getTopFields(undefined, undefined, COUNTRY, condition);
    }

    getVideoInfo(videoId) {
        let tags = Array.from(this.cache.getFieldsForField(TAG, YOUTUBE, videoId));
        let styles = Array.from(this.cache.getFieldsForField(STYLE, YOUTUBE, videoId));
        let years = Array.from(this.cache.getFieldsForField(YEAR, YOUTUBE, videoId));

        return {
            tags: tags.map(x => x.value),
            styles: styles.map(x => x.value),
            year: years.map(x => x.value)[0],
        };
    }

    async getTopFields(start, size, fieldType, condition=AND) {
        let selectedFields = this.getSelectedFields(condition);

        if (this.empty()) {
            return normalize(
                this.cache.getAllForField(fieldType, start, size));
        }

        let list = [];
        for (let selectedFieldType in selectedFields) {
            selectedFields[selectedFieldType].forEach(field => {
                list.push(this.cache.getFieldsForField(fieldType, selectedFieldType, field)); 
            });
        }

        let results = [];
        if (condition == OR) {
            // union them together
            results = Array.from(this.unionAll(list));
        } else {
            results = Array.from(this.intersectAll(list));
        }

        return normalize(results);
    }

    getSelectedFields(condition=AND) {
        let years = this.year === undefined ? [] : [this.year];
        return {
            TAG: condition == OR ? this.selectedOrTags : this.selectedTags,
            STYLE: this.selectedStyles,
            COUNTRY: this.selectedCountries,
            YEAR: years
        };
    }

    async getTagsForSounds(sounds) {
        let tags = {};
        let tagsForSounds:Promise<void>[] = [];

        for (let i in sounds) {
            tagsForSounds.push(ethereum.getTagsForSound(sounds[i]));
        }

        let tagsForSoundsResolved = await Promise.all(tagsForSounds);

       for (let i in tagsForSoundsResolved) {
            let tagsForSound = tagsForSoundsResolved[i];
            tagsForSound.forEach(tag => tags[tag] = true);
        }

        return Object.keys(tags);
    }

    // following functions are helpers for union and intersection
    unionAll(list) {
        if (list === undefined || list.length == 0) {
            return [];
        }
        let union = {};
        list.forEach(
            s => 
                s.forEach(
                    r => {
                        if (!(r.value in union)) {
                            union[r.value] = 0;
                        }
                        union[r.value] += r.rank;
                    }));
        return union;
    }

    intersectAll(list) {
        if (list === undefined || list.length == 0) {
            return [];
        }
        let listMap = [];;
        for (let i in list) {
            var a = list[i];
            let b = {};
            a.forEach(j => b[j.value] = j.rank);
            listMap.push(b);
        }
        let resolved = listMap[0];

        for (let i=1; i < listMap.length; i++) {
            resolved = intersect(resolved, listMap[i]);
        }
        let out = new Set();
        for (let i in resolved) {
            out.add({
                value: i, rank: resolved[i] / listMap.length
            });
        }
        return out; 
    }
}

function normalize(list) {
    list = list.map(x => ({
        value: x.value,
        rank: Math.pow(0.9, 8 * x.rank / list.length)
    }));
    let max = Math.max.apply(Math, list.map(x => x.rank));
    return list.map(x => ({value: x.value, rank: x.rank / max}));
}

function intersect(set1, set2) {
    let out = {};
    for (let i in set1) {
        if (i in set2) {
            out[i] = set1[i].rank + set2[i].rank;
        }
    }
    return out;
}

export default EthereumFieldSearcher;
